import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  createApartment, 
  deleteApartment,
  updateApartment,
  createTenant,
  updateTenant,
  removeResidentFromApartment,
  Apartment,
  Property
} from '../lib/api';
import { useApartments, useProperties, useInvalidateQueries } from '../hooks/useApiQueries';
import { 
  House,
  Trash,
  User,
  CaretDown,
  CaretRight,
  Phone,
  EnvelopeSimple,
  IdentificationCard,
  PencilSimple,
  UserPlus,
  Warehouse
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  EditApartmentForm,
  EditResidentForm,
  NewApartmentForm,
  NewResidentForm,
  formatPhoneNumber
} from '../components/apartments';
import { AddApartmentDialog } from '../components/apartments/AddApartmentDialog';
import { AddResidentDialog } from '../components/apartments/AddResidentDialog';
import { EditResidentDialog } from '../components/apartments/EditResidentDialog';
import { EditApartmentDialog } from '../components/apartments/EditApartmentDialog';

// Format personal number input: xxxxxxxx-xxxx
const formatPersonalNumberInput = (value: string | undefined): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 8) return digits;
  return `${digits.slice(0, 8)}-${digits.slice(8, 12)}`;
};

// Display personal number: xxxxxxxx-**** (always masked)
const displayPersonalNumber = (value: string | undefined): string => {
  if (!value) return '';
  const clean = value.replace(/\D/g, '');
  if (clean.length <= 8) return clean;
  return `${clean.slice(0, 8)}-****`;
};

// Get only first 8 digits for editing (last 4 are encrypted and not shown)
const getEditablePersonalNumber = (value: string | undefined): string => {
  if (!value) return '';
  const clean = value.replace(/\D/g, '');
  return clean.slice(0, 8);
};

export default function ApartmentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { invalidateApartments } = useInvalidateQueries();
  
  // State for filters and UI
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [expandedApartments, setExpandedApartments] = useState<Record<string, boolean>>({});
  
  // React Query hooks
  const { data: apartmentsData = [], isLoading: loading, refetch: refetchApartments } = useApartments(
    user?.organization_id,
    true
  );
  const { data: propertiesData = [], refetch: refetchProperties } = useProperties(
    user?.organization_id,
    true
  );
  
  // Filter apartments by selected property
  const apartments: Apartment[] = selectedProperty !== 'all'
    ? apartmentsData.filter((a: Apartment) => a.property_id === selectedProperty)
    : apartmentsData;
  const properties: Property[] = propertiesData;
  
  // Refetch helper
  const fetchData = useCallback(() => {
    invalidateApartments();
    refetchApartments();
    refetchProperties();
  }, [invalidateApartments, refetchApartments, refetchProperties]);
  
  // Apartment form
  const [formData, setFormData] = useState<NewApartmentForm>({
    property_id: '',
    apartment_number: '',
    floor: '',
    dwelling_type: 'apartment',
    area_sqm: '',
    storage_units: []
  });

  // Edit apartment
  const [editApartmentOpen, setEditApartmentOpen] = useState(false);
  const [editingApartment, setEditingApartment] = useState<EditApartmentForm | null>(null);

  // Resident management
  const [addResidentOpen, setAddResidentOpen] = useState(false);
  const [editResidentOpen, setEditResidentOpen] = useState(false);
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(null);
  const [editingResident, setEditingResident] = useState<EditResidentForm | null>(null);
  const [newResident, setNewResident] = useState<NewResidentForm>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    personal_number: '',
    role: 'resident'
  });

  // Memoize sorted apartments to avoid re-sorting on every render
  const sortedApartments = useMemo(() => {
    return [...apartments].sort((a, b) => {
      const numA = a.apartment_number || '';
      const numB = b.apartment_number || '';
      return numA.localeCompare(numB, 'sv', { numeric: true });
    });
  }, [apartments]);

  const handleCreate = async () => {
    if (!formData.property_id || !formData.apartment_number) {
      toast.error('Fyll i fastighet och lägenhetsnummer');
      return;
    }
    
    try {
      await createApartment({
        ...formData,
        organization_id: user?.organization_id,
        floor: formData.floor ? parseInt(formData.floor) : undefined,
        area_sqm: formData.area_sqm ? parseFloat(formData.area_sqm) : undefined,
        storage_units: formData.storage_units || []
      });
      toast.success(t('saved_successfully'));
      setDialogOpen(false);
      setFormData({
        property_id: '',
        apartment_number: '',
        floor: '',
        dwelling_type: 'apartment',
        area_sqm: '',
        storage_units: []
      });
      fetchData();
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Är du säker?')) return;
    
    try {
      await deleteApartment(id);
      toast.success('Lägenheten har raderats');
      fetchData();
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  const toggleExpand = (aptId: string) => {
    setExpandedApartments(prev => ({
      ...prev,
      [aptId]: !prev[aptId]
    }));
  };

  const getPropertyName = (propertyId: string | undefined) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop?.name || propertyId;
  };

  const getDwellingTypeLabel = (type: string | undefined): string => {
    const labels: Record<string, string> = {
      apartment: 'Lägenhet',
      house: 'Hus',
      townhouse: 'Radhus',
      studio: 'Etta'
    };
    return (type && labels[type]) || type || '';
  };

  // Apartment edit functions
  const openEditApartment = (apt: Apartment, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingApartment({
      ...apt,
      floor: apt.floor || '',
      area_sqm: apt.area_sqm || '',
      storage_units: apt.storage_units || []
    });
    setEditApartmentOpen(true);
  };

  const handleEditApartment = async () => {
    if (!editingApartment || !editingApartment.apartment_number) {
      toast.error('Fyll i lägenhetsnummer');
      return;
    }

    try {
      const floorValue = editingApartment.floor 
        ? (typeof editingApartment.floor === 'string' ? parseInt(editingApartment.floor) : editingApartment.floor) 
        : undefined;
      const areaValue = editingApartment.area_sqm 
        ? (typeof editingApartment.area_sqm === 'string' ? parseFloat(editingApartment.area_sqm) : editingApartment.area_sqm) 
        : undefined;
        
      await updateApartment(editingApartment.id, {
        property_id: editingApartment.property_id,
        apartment_number: editingApartment.apartment_number,
        floor: floorValue,
        dwelling_type: editingApartment.dwelling_type,
        area_sqm: areaValue,
        storage_units: editingApartment.storage_units || []
      });
      
      toast.success('Lägenheten uppdaterad');
      setEditApartmentOpen(false);
      setEditingApartment(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update apartment:', error);
      toast.error('Kunde inte uppdatera lägenheten');
    }
  };

  // Resident management functions
  const openAddResident = (aptId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedApartmentId(aptId);
    setNewResident({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      personal_number: '',
      role: 'resident'
    });
    setAddResidentOpen(true);
  };

  const openEditResident = (resident: { id: string; name?: string; first_name?: string; last_name?: string; email?: string; phone?: string; personal_number?: string; role?: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingResident({
      id: resident.id,
      first_name: resident.first_name || resident.name?.split(' ')[0] || '',
      last_name: resident.last_name || resident.name?.split(' ').slice(1).join(' ') || '',
      email: resident.email || '',
      phone: resident.phone || '',
      personal_number: resident.personal_number || '',
      role: (resident.role || 'resident') as EditResidentForm['role']
    });
    setEditResidentOpen(true);
  };

  const handleAddResident = async () => {
    if (!newResident.first_name || !newResident.last_name || !newResident.email) {
      toast.error('Fyll i förnamn, efternamn och e-post');
      return;
    }

    try {
      // Create the tenant with apartment_id and organization_id
      await createTenant({
        ...newResident,
        apartment_id: selectedApartmentId || undefined,
        organization_id: user?.organization_id
      });
      
      toast.success('Boende tillagd');
      setAddResidentOpen(false);
      setSelectedApartmentId(null);
      fetchData();
    } catch (error: unknown) {
      console.error('Failed to add resident:', error);
      const axiosError = error as { response?: { data?: { detail?: string } } };
      toast.error(axiosError.response?.data?.detail || 'Kunde inte lägga till boende');
    }
  };

  const handleEditResident = async () => {
    if (!editingResident || !editingResident.first_name || !editingResident.last_name || !editingResident.email) {
      toast.error('Fyll i förnamn, efternamn och e-post');
      return;
    }

    try {
      await updateTenant(editingResident.id, {
        first_name: editingResident.first_name,
        last_name: editingResident.last_name,
        email: editingResident.email,
        phone: editingResident.phone,
        personal_number: editingResident.personal_number,
        role: editingResident.role as 'superadmin' | 'admin' | 'auditor' | 'suppliant' | 'board_member' | 'resident' | 'supplier' | 'external'
      });
      
      toast.success('Boende uppdaterad');
      setEditResidentOpen(false);
      setEditingResident(null);
      fetchData();
    } catch (error: unknown) {
      console.error('Failed to update resident:', error);
      const axiosError = error as { response?: { data?: { detail?: string } }; message?: string };
      const errorMsg = axiosError.response?.data?.detail || axiosError.message || 'Kunde inte uppdatera boende';
      toast.error(errorMsg);
    }
  };

  const handleRemoveResident = async (residentId: string, aptId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Är du säker på att du vill ta bort denna boende från lägenheten?')) return;

    try {
      await removeResidentFromApartment(aptId, residentId);
      toast.success('Boende borttagen från lägenheten');
      fetchData();
    } catch (error) {
      console.error('Failed to remove resident:', error);
      toast.error('Kunde inte ta bort boende');
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="apartments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {t('apartments')}
        </h1>
        <AddApartmentDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          form={formData}
          onFormChange={setFormData}
          onSave={handleCreate}
          properties={properties}
          t={t}
        />
      </div>

      {/* Filter */}
      <div className="card-flat">
        <div className="flex items-center gap-4">
          <Label className="text-sm text-slate-500">Filtrera:</Label>
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="w-[200px]" data-testid="property-filter">
              <SelectValue placeholder="Alla fastigheter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla fastigheter</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="card-flat animate-pulse">
              <div className="h-5 w-32 bg-slate-200 rounded mb-2" />
              <div className="h-4 w-48 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      ) : apartments.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state">
            <House size={48} className="empty-state-icon" />
            <p className="text-slate-500">Inga lägenheter ännu</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedApartments.map((apt) => {
            const isExpanded = expandedApartments[apt.id];
            const hasResidents = apt.residents && apt.residents.length > 0;
            
            return (
              <div
                key={apt.id}
                className="card-flat overflow-hidden"
                data-testid={`apartment-card-${apt.id}`}
              >
                {/* Apartment Header - Clickable */}
                <div
                  className="flex items-center gap-4 cursor-pointer select-none"
                  onClick={() => toggleExpand(apt.id)}
                >
                  <div className="text-slate-400">
                    {isExpanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
                  </div>
                  
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <House size={20} className="text-slate-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-900">
                        {apt.apartment_number}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-slate-100 rounded text-slate-600">
                        {getDwellingTypeLabel(apt.dwelling_type)}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500">
                      {getPropertyName(apt.property_id)}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    {apt.floor && (
                      <span>Vån {apt.floor}</span>
                    )}
                    {apt.area_sqm && (
                      <span>{apt.area_sqm} m²</span>
                    )}
                    {(apt.storage_units?.length ?? 0) > 0 && (
                      <span className="flex items-center gap-1" title={apt.storage_units?.join(', ')}>
                        <Warehouse size={14} />
                        {apt.storage_units?.length}
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <User size={16} />
                      <span>{apt.residents?.length || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => openEditApartment(apt, e)}
                      className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Redigera lägenhet"
                      data-testid={`edit-apartment-${apt.id}`}
                    >
                      <PencilSimple size={18} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(apt.id, e)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Ta bort lägenhet"
                      data-testid={`delete-apartment-${apt.id}`}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    {/* Apartment Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <span className="text-xs text-slate-500">Fastighet</span>
                        <p className="text-sm font-medium text-slate-900">{getPropertyName(apt.property_id)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Våning</span>
                        <p className="text-sm font-medium text-slate-900">{apt.floor || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Yta</span>
                        <p className="text-sm font-medium text-slate-900">{apt.area_sqm ? `${apt.area_sqm} m²` : '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Typ</span>
                        <p className="text-sm font-medium text-slate-900">{getDwellingTypeLabel(apt.dwelling_type)}</p>
                      </div>
                    </div>
                    
                    {/* Storage Units (Förråd) */}
                    {(apt.storage_units?.length ?? 0) > 0 && (
                      <div className="mb-4 p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm font-medium text-amber-800 mb-2">
                          <Warehouse size={16} />
                          Förråd
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {apt.storage_units?.map((storage, idx) => (
                            <span 
                              key={idx} 
                              className="px-2 py-1 bg-amber-100 text-amber-900 text-sm rounded"
                            >
                              {storage}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Residents */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-slate-700">
                          Boende ({apt.residents?.length || 0})
                        </h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => openAddResident(apt.id, e)}
                          data-testid={`add-resident-${apt.id}`}
                        >
                          <UserPlus size={16} className="mr-1" />
                          Lägg till boende
                        </Button>
                      </div>
                      
                      {!hasResidents ? (
                        <p className="text-sm text-slate-500 italic">Inga boende registrerade</p>
                      ) : (
                        <div className="space-y-3">
                          {apt.residents?.map((resident) => (
                            <div
                              key={resident.id}
                              className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg group"
                              data-testid={`resident-${resident.id}`}
                            >
                              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                <User size={20} className="text-slate-500" />
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-slate-900">{resident.first_name || resident.name?.split(' ')[0]} {resident.last_name || resident.name?.split(' ').slice(1).join(' ')}</span>
                                  <span className="text-xs px-2 py-0.5 bg-slate-200 rounded text-slate-600">
                                    {resident.role === 'admin' ? 'Admin' : 
                                     resident.role === 'board_member' ? 'Styrelse' : 'Boende'}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                                  {resident.email && (
                                    <a 
                                      href={`mailto:${resident.email}`}
                                      className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <EnvelopeSimple size={14} />
                                      <span className="truncate">{resident.email}</span>
                                    </a>
                                  )}
                                  {resident.phone && (
                                    <a 
                                      href={`tel:${resident.phone}`}
                                      className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Phone size={14} />
                                      <span>{resident.phone}</span>
                                    </a>
                                  )}
                                  {resident.personal_number && (
                                    <div className="flex items-center gap-1.5 text-slate-500">
                                      <IdentificationCard size={14} />
                                      <span className="font-mono text-xs">
                                        {displayPersonalNumber(resident.personal_number)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Edit/Delete buttons */}
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={(e) => openEditResident(resident, e)}
                                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                  title="Redigera"
                                >
                                  <PencilSimple size={16} />
                                </button>
                                <button
                                  onClick={(e) => handleRemoveResident(resident.id, apt.id, e)}
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Ta bort"
                                >
                                  <Trash size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Resident Dialog */}
      <AddResidentDialog
        open={addResidentOpen}
        onOpenChange={setAddResidentOpen}
        form={newResident}
        onFormChange={setNewResident}
        onSave={handleAddResident}
        formatPersonalNumberInput={formatPersonalNumberInput}
      />

      {/* Edit Resident Dialog */}
      <EditResidentDialog
        open={editResidentOpen}
        onOpenChange={setEditResidentOpen}
        resident={editingResident}
        onResidentChange={setEditingResident}
        onSave={handleEditResident}
        getEditablePersonalNumber={getEditablePersonalNumber}
      />

      {/* Edit Apartment Dialog */}
      <EditApartmentDialog
        open={editApartmentOpen}
        onOpenChange={setEditApartmentOpen}
        apartment={editingApartment}
        onApartmentChange={setEditingApartment}
        onSave={handleEditApartment}
        properties={properties}
        t={t}
      />
    </div>
  );
}
